import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useMeta } from "@/hooks/useMeta";
import { faqArticleConfigs } from "@/config/faqConfigs";
import { lifeEventConfigs } from "@/config/lifeEventConfigs";

const CATEGORIES = [
  {
    title: "Verkaufsprozess",
    description: "Alles rund um den erfolgreichen Immobilienverkauf",
    items: [
      { slug: "wie-laeuft-immobilienverkauf", source: "faq" },
      { slug: "welche-unterlagen-immobilienverkauf", source: "faq" },
      { slug: "wann-immobilie-verkaufen", source: "faq" },
      { slug: "makler-oder-privatverkauf", source: "faq" },
      { slug: "immobilie-verkaufen-kosten-und-steuern", source: "life" },
      { slug: "notartermin-immobilienverkauf", source: "life" },
    ],
  },
  {
    title: "Bewertung & Finanzierung",
    description: "Marktwert, Energieausweis und Finanzierungstipps",
    items: [
      { slug: "wie-wird-immobilie-bewertet", source: "faq" },
      { slug: "energieausweis-immobilienverkauf", source: "life" },
      { slug: "immobilienfinanzierung-kreis-mettmann", source: "life" },
      { slug: "immobilie-als-kapitalanlage", source: "life" },
    ],
  },
  {
    title: "Lebenssituationen",
    description: "Immobilienentscheidungen bei Veränderungen im Leben",
    items: [
      { slug: "immobilie-verkaufen-bei-scheidung", source: "life" },
      { slug: "immobilie-verkaufen-im-alter", source: "life" },
      { slug: "immobilie-kaufen-bei-jobwechsel", source: "life" },
      { slug: "immobilie-kaufen-bei-familienzuwachs", source: "life" },
      { slug: "immobilie-bei-familiaeren-veraenderungen", source: "life" },
      { slug: "immobilie-verkaufen-bei-erbschaft", source: "life" },
      { slug: "immobilie-verkaufen-umzug-ausland", source: "life" },
      { slug: "immobilie-bei-pflegebeduerftigkeit", source: "life" },
    ],
  },
  {
    title: "Wohnen & Modernisierung",
    description: "Altersgerechtes Wohnen, Sanierung und Barrierefreiheit",
    items: [
      { slug: "wohnen-im-alter-kreis-mettmann", source: "life" },
      { slug: "barrierefreies-wohnen-kreis-mettmann", source: "life" },
      { slug: "immobilie-verkaufen-bei-modernisierungsstau", source: "life" },
    ],
  },
];

function getConfig(slug, source) {
  if (source === "faq") return faqArticleConfigs[slug];
  return lifeEventConfigs[slug];
}

export default function RatgeberPage() {
  useMeta(
    "Ratgeber Immobilien | BRIMO Immobilien NRW",
    "Immobilien-Ratgeber von BRIMO: Verkaufsprozess, Finanzierung, Lebenssituationen, Energieausweis und mehr. Expertenwissen für Kreis Mettmann."
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="ratgeber-page">
      <Navbar />

      <section className="pt-32 pb-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight size={10} />
            <span className="text-white">Ratgeber</span>
          </nav>
          <div className="h-0.5 w-10 bg-[#E61D38] mb-4" />
          <h1
            className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tight leading-tight mb-4"
            style={{ fontFamily: "Montserrat, sans-serif" }}
            data-testid="ratgeber-h1"
          >
            Immobilien-Ratgeber
          </h1>
          <p className="text-gray-400 text-base max-w-2xl mb-4">
            Fundiertes Wissen rund um Immobilien im Kreis Mettmann. Von Verkaufsprozess und Finanzierung bis zu Lebenssituationen wie Scheidung, Erbschaft oder Familienzuwachs.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="space-y-12">
            {CATEGORIES.map((cat, ci) => (
              <div key={ci} data-testid={`ratgeber-category-${ci}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-0.5 w-6 bg-[#E61D38]" />
                  <h2
                    className="text-lg font-bold text-white uppercase tracking-wide"
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    {cat.title}
                  </h2>
                </div>
                <p className="text-gray-500 text-sm mb-5 ml-9">{cat.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ml-9">
                  {cat.items.map(({ slug, source }) => {
                    const cfg = getConfig(slug, source);
                    if (!cfg) return null;
                    return (
                      <Link
                        key={slug}
                        to={`/${slug}`}
                        className="group bg-[#0d0d0d] border border-white/5 p-5 hover:border-[#E61D38]/30 transition-colors"
                        data-testid={`ratgeber-link-${slug}`}
                      >
                        <h3 className="text-white text-sm font-bold mb-2 group-hover:text-[#E61D38] transition-colors">
                          {cfg.h1}
                        </h3>
                        <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3">
                          {cfg.description}
                        </p>
                        <span className="flex items-center gap-1.5 text-[#E61D38] text-[10px] font-bold uppercase tracking-wider">
                          Weiterlesen
                          <ArrowRight size={10} className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
